import React from 'react';
import { Link } from 'react-router-dom';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useCart } from '../../context/CartContext';
import type { CartItem } from '../../context/CartContext';

const WHATSAPP_NUMBER = '263774134805';

function buildWhatsAppEnquiryMessage(items: CartItem[]): string {
  const lines = items.map((i) => `• ${i.name} (Qty: ${i.quantity})`);
  return [
    'Hi, I would like to request a quote for the following items:',
    '',
    ...lines,
    '',
    'Thank you.',
  ].join('\n');
}

const Enquiry: React.FC = () => {
  const { items, count, removeItem, updateQuantity, clearCart } = useCart();

  const handleSubmitEnquiry = () => {
    const text = buildWhatsAppEnquiryMessage(items);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (count === 0) {
    return (
      <main className="min-h-screen pt-24 bg-[#FFFFFE]">
        <div className="container-main py-16 md:py-24">
          <div className="max-w-2xl mx-auto p-8 md:p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-primary/5">
              <ShoppingBagOutlinedIcon sx={{ fontSize: 48 }} className="text-primary/50" />
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-primary tracking-tight mb-3">
              Your enquiry list is empty
            </h1>
            <p className="text-gray-500 text-[15px] leading-relaxed mb-8">
              Add products from the shop to request a quote. Your list is saved so you can come back anytime.
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-primary text-white font-bold py-3.5 px-7 rounded-full text-sm shadow-lg hover:bg-primary-light transition-all"
            >
              Browse products <ArrowForwardIcon fontSize="small" />
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-24 pb-16 bg-[#FFFFFE]">
      <div className="container-main py-8 md:py-12 max-w-6xl">
        {/* Header - full page, no card */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 md:pb-8 border-b border-gray-200">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-primary tracking-tight">
                Your enquiry list
              </h1>
              <p className="text-gray-500 text-sm mt-1">
                {count} {count === 1 ? 'item' : 'items'} · Request a quote when ready
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={clearCart}
                className="text-sm font-semibold text-gray-500 hover:text-red-600 transition-colors py-2.5 px-4 rounded-full hover:bg-red-50"
              >
                Clear all
              </button>
              <Link
                to="/contact"
                className="text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 py-2.5 px-5 rounded-full transition-colors"
              >
                Let’s talk
              </Link>
            </div>
        </div>

        <div className="pt-6 md:pt-8 flex flex-col lg:flex-row gap-8">
          {/* Left: Cart items (table-like) - scrollable when many items */}
            <div className="flex-1 min-w-0">
              <div className="max-h-[70vh] flex flex-col min-h-0">
                {/* Table headers - at top of scroll area */}
                <div className="hidden md:grid grid-cols-12 gap-4 pb-3 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wider flex-shrink-0">
                  <div className="col-span-5">Product</div>
                  <div className="col-span-3 text-center">Quantity</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>

                <ul className="divide-y divide-gray-100 overflow-y-auto flex-1 min-h-0 hide-scrollbar">
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-4 py-5 md:py-4 md:items-center"
                  >
                    <div className="md:col-span-5 flex gap-4">
                      <div
                        className="w-20 h-20 flex-shrink-0 rounded-xl flex items-center justify-center overflow-hidden bg-[#F7F9FC] border border-gray-100"
                      >
                        {item.img ? (
                          <img
                            src={item.img}
                            alt={item.name}
                            className="w-full h-full object-contain p-2"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <MedicalServicesIcon className="text-primary/30" sx={{ fontSize: 32 }} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 truncate">{item.name}</p>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {item.tag || item.cat || 'Medical supplies'}
                        </p>
                      </div>
                    </div>

                    <div className="md:col-span-3 flex items-center gap-2 md:justify-center">
                      <div className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50/80 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-2.5 text-gray-600 hover:bg-gray-200/80 transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <RemoveIcon fontSize="small" />
                        </button>
                        <span className="w-10 text-center text-sm font-bold text-gray-800 tabular-nums">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-2.5 text-gray-600 hover:bg-gray-200/80 transition-colors"
                          aria-label="Increase quantity"
                        >
                          <AddIcon fontSize="small" />
                        </button>
                      </div>
                    </div>

                    <div className="md:col-span-2 flex items-center md:justify-end">
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="p-2.5 text-gray-400 hover:text-red-600 transition-colors rounded-full hover:bg-red-50"
                        aria-label="Remove"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>
                  </li>
                ))}
                </ul>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 flex-shrink-0">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 text-sm font-bold text-primary border-2 border-primary py-2.5 px-5 rounded-full hover:bg-primary hover:text-white transition-colors"
                >
                  Continue shopping
                </Link>
              </div>
            </div>

          {/* Right: Order summary */}
          <div className="lg:w-[340px] flex-shrink-0">
            <div className="p-6 bg-gray-50/80 border border-gray-200 rounded-xl h-full flex flex-col">
                <h2 className="text-lg font-bold text-primary mb-5">Order summary</h2>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Items</span>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4 flex items-center gap-1.5">
                  <span>CE certified · Request quote for pricing</span>
                </p>
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={handleSubmitEnquiry}
                    className="w-full py-3.5 px-6 rounded-full bg-primary text-white font-bold text-sm shadow-lg hover:bg-primary-light transition-all inline-flex items-center justify-center gap-2"
                  >
                    Send enquiry via WhatsApp <ArrowForwardIcon fontSize="small" />
                  </button>
                </div>
            </div>
          </div>
        </div>

        {/* CTA strip - full width section */}
        <div className="mt-10 md:mt-12 py-6 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500 font-medium">Need a custom or bulk quote?</p>
            <p className="text-xl font-bold text-primary mt-0.5">Request your enquiry</p>
          </div>
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 py-2.5 px-6 rounded-full transition-colors w-fit inline-block"
          >
            Let’s talk
          </a>
        </div>
      </div>
    </main>
  );
};

export default Enquiry;
